/**
 * "Hypotézy" on the Investigations page, filled from the API: the list and, for a collaborator,
 * the create dialog, which opens the new hypothesis once it is made. With the case's
 * *Spis k dátumu* set, the list is as it stood on that day, under its banner, and takes no new
 * hypothesis (PRD-0075 R-D4).
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreateHypothesisDialog } from '@/components/analytic-hypotheses/CreateHypothesisDialog'
import {
  type HypothesesSectionState,
  HypothesesSectionView,
} from '@/components/analytic-hypotheses/HypothesesSectionView'
import { useCaseAsOf } from '@/features/case-as-of/CaseAsOf'
import { CaseAsOfBanner } from '@/features/case-as-of/CaseAsOfBanner'
import { useEntitySearch } from '@/features/entity-picker/useEntitySearch'
import type { AnalyticHypothesisCreate } from '@/types/analytic-hypothesis'
import { hypothesisPath } from './paths'
import { messageOf, useAnalyticHypotheses, useHypothesisWrites } from './queries'

export function HypothesesSection({ caseId }: { caseId: string }) {
  const navigate = useNavigate()
  const { day, asOf } = useCaseAsOf()
  const query = useAnalyticHypotheses(caseId, asOf)
  const writes = useHypothesisWrites(caseId)
  const entitySearch = useEntitySearch(caseId, 'focus')
  const [creating, setCreating] = useState(false)
  // A list already read stays on screen when a refetch fails.
  const view: HypothesesSectionState = query.data
    ? { status: 'ready', hypotheses: query.data.hypotheses }
    : query.isError
      ? { status: 'failed' }
      : { status: 'loading' }
  // The file as of a past day is read-only: what is written now would not show in it.
  const canWrite = query.data !== undefined && query.data.member_role !== 'viewer' && !day

  const create = async (body: AnalyticHypothesisCreate) => {
    const outcome = await writes.create(body)
    if (outcome.value) navigate(hypothesisPath(caseId, outcome.value.id))
    return messageOf(outcome)
  }

  return (
    <>
      <CaseAsOfBanner className="mb-3" />
      <HypothesesSectionView
        view={view}
        hypothesisHref={id => hypothesisPath(caseId, id)}
        onCreate={canWrite ? () => setCreating(true) : null}
        onRetry={() => void query.refetch()}
      />
      {canWrite && (
        <CreateHypothesisDialog
          open={creating}
          onOpenChange={setCreating}
          onSubmit={create}
          entitySearch={entitySearch}
        />
      )}
    </>
  )
}
